import type { UserClaims } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { activeRecord } from '../lib/graphql/common/queries';
import { genericScalars } from '../lib/graphql/helpers';
import { objValueNullToUndefined } from '../lib/utils';
import { userWithCalendarIdSelector } from './selectors/user';
import type { StoredUserWithCalendarId } from './types';

type UserCache = {
  // Cache item is mutable.
  loggedInUser?: UserClaims;
};

const memoryCache: UserCache = {
  loggedInUser: undefined,
};

/**
 * Retrieves current and previous user information from Okta provided react hook.
 * Note that retrieving user info from Okta is an async call and requires network request.
 * TODO: consider proper cache the user information in client browser, currently it is only cached in memory.
 * @returns The current and previous user information.
 */
export const useLoggedInUser = () => {
  const { oktaAuth, authState } = useOktaAuth();

  const [oktaUser, setUserInfo] = useState<UserClaims | undefined>(undefined);

  const { data: userQueryResult, previousData } = useTypedQuery(
    {
      user: [
        {
          where: {
            ...activeRecord,
            external_id: {
              _eq: oktaUser?.sub ?? '',
            },
          },
        },
        userWithCalendarIdSelector,
      ],
    },
    {
      scalars: genericScalars,
    }
  );

  const loggedInUser = objValueNullToUndefined<StoredUserWithCalendarId>(
    userQueryResult?.user[0]
  );

  const accessToken = oktaAuth.getAccessToken();
  useEffect(() => {
    if (authState?.isAuthenticated === true && accessToken !== undefined) {
      const fetchUser = async () => {
        const user = await oktaAuth.getUser().catch((e) => {
          console.log(JSON.stringify(e, null, 2));
          // eslint-disable-next-line functional/no-throw-statements
          throw e;
        });

        setUserInfo(user);

        // Cache item is mutable.
        // eslint-disable-next-line functional/immutable-data
        memoryCache.loggedInUser = user;
      };

      if (memoryCache.loggedInUser === undefined) {
        void fetchUser();
      } else {
        setUserInfo(memoryCache.loggedInUser);
      }
    }
  }, [oktaAuth, accessToken, authState?.isAuthenticated]);

  return { loggedInUser, previousData };
};
